import { selectedFlowIdState } from '@/state';
import type { AppFlowParams } from '@rock/types';
import { useSetAtom } from 'jotai';
import { createContext, useCallback, useContext, useTransition } from 'react';
import { useNavigate } from 'react-router-dom';

/**
 * Where this mount's guides live in the URL (`/spaces/hvac/guides`), or undefined when the mount
 * isn't URL-addressed — the Guides table raises the same dialogs but stays where it is.
 *
 * It lives here rather than beside the provider so the things that consume it don't have to import
 * `FlowInitializer`, which renders one of them.
 */
export const GuideEditBaseContext = createContext<string | undefined>(undefined);

/**
 * Opens a guide in the editor.
 *
 * Where the editor is URL-addressed this is a navigation, so the address bar keeps naming the guide
 * you're actually looking at and a reload or a pasted link lands back on it. `replace`, because the
 * back arrow is for leaving the editor — not for walking back through every guide you glanced at.
 */
export function useSelectFlow(params: AppFlowParams) {
    const editBase = useContext(GuideEditBaseContext);
    const setSelectedFlowId = useSetAtom(selectedFlowIdState(params));
    const navigate = useNavigate();
    const [, startTransition] = useTransition();

    return useCallback(
        (flowId: string) => {
            if (editBase) {
                navigate(`${editBase}/${flowId}`, { replace: true });
                return;
            }
            startTransition(() => setSelectedFlowId(flowId));
        },
        [editBase, navigate, setSelectedFlowId],
    );
}
